import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, ArrowLeft, MapPin, Upload, Download } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../utils/constants';

import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet's default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

const C = COLORS;

const LocationPicker = ({ lat, lng, onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });

  return lat && lng ? <Marker position={[lat, lng]} /> : null;
};

const SchoolForm = () => {
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const isTeacherType = user?.user_type === 'Teacher';
  const hideBulkAndTemplate = isManager || isTeacherType;

  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileInputRef = React.useRef(null);

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    udise: '',
    board_id: '',
    dsc_code: '',
    street1: '',
    street2: '',
    block: '',
    landmark: '',
    city: '',
    zip: '',
    state: '',
    email: '',
    fax: '',
    contact1: '',
    contact2: '',
    contact_person: '',
    district_id: '',
    work_id: '',
    lat: 28.6139,
    lng: 77.2090, // Default to New Delhi
    status: 'Active',
    school_type: ''
  });

  useEffect(() => {
    fetchBoards();
    if (isEdit) {
      fetchSchool();
    }
  }, [id]);

  const fetchBoards = async () => {
    try {
      const res = await api.get('/config/board');
      setBoards(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSchool = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/schools/${id}`);
      if (res.data) {
        setFormData({
          ...res.data,
          lat: parseFloat(res.data.lat) || 28.6139,
          lng: parseFloat(res.data.lng) || 77.2090,
          board_id: res.data.board_id || '',
          status: res.data.status || 'Active',
          school_type: res.data.school_type || ''
        });
      }
    } catch (err) {
      toast.error('Failed to load school');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      return toast.error('School Name is required');
    }
    
    try {
      if (isEdit) {
        await api.put(`/schools/${id}`, formData);
        toast.success('School updated successfully');
      } else {
        await api.post('/schools', formData);
        toast.success('School added successfully');
      }
      navigate('/schools');
    } catch (err) {
      toast.error('Failed to save school');
    }
  };

  const handleLocationSelect = (lat, lng) => {
    setFormData({ ...formData, lat, lng });
  };

  const handleFileUpload = async (e) => {
    if (hideBulkAndTemplate) {
      toast.error('Bulk upload is not permitted for your role.');
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    const payload = new FormData();
    payload.append('file', file);

    try {
      const toastId = toast.loading('Uploading schools...');
      await api.post('/schools/upload', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Schools uploaded successfully', { id: toastId });
      navigate('/schools');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload schools');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = "School Name,UDISE,Board ID,School Type,Status,DSC Code,Street 1,Street 2,Block,Landmark,City,State,ZIP/Postal Code,District ID,Email Address,Fax,Contact 1,Contact 2,Contact Person,Work ID,Lat,Lng\nDelhi Public School,07080100101,1,Private,Active,DSC123,Main St,,Sector 1,,New Delhi,Delhi,110001,D1,,,,,Mr John,,28.61,77.20";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "School_Bulk_Upload_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <AppLayout title="Edit School"><div style={{ padding: 40, textAlign: 'center' }}>Loading...</div></AppLayout>;

  return (
    <AppLayout title={isEdit ? 'Edit School' : 'Add School'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => navigate('/schools')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: C.g700 }}
        >
          <ArrowLeft size={18} /> Back
        </button>
        
        {!isEdit && !hideBulkAndTemplate && (
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#fff', color: C.g700, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              <Download size={18} /> Template
            </button>
            <input type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
            <button type="button" onClick={() => fileInputRef.current.click()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: C.g50, color: C.g800, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <Upload size={18} /> Bulk Upload
            </button>
          </div>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, padding: 32 }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
            {/* Basic Info */}
            <div style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>Basic Information</h3>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>School Name *</label>
              <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Enter school name" required />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>UDISE</label>
              <input type="text" value={formData.udise} onChange={e => setFormData({...formData, udise: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="UDISE code" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Board</label>
              <select value={formData.board_id} onChange={e => setFormData({...formData, board_id: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="">Select Board</option>
                {boards.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>School Type</label>
              <select value={formData.school_type} onChange={e => setFormData({...formData, school_type: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="">Select School Type</option>
                <option value="GOVT">GOVT</option>
                <option value="Private">Private</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Status</label>
              <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>DSC Code</label>
              <input type="text" value={formData.dsc_code} onChange={e => setFormData({...formData, dsc_code: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="DSC code" />
            </div>

            {/* Address */}
            <div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>Address</h3>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Street 1</label>
              <input type="text" value={formData.street1} onChange={e => setFormData({...formData, street1: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Street address" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Street 2</label>
              <input type="text" value={formData.street2} onChange={e => setFormData({...formData, street2: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Apt, suite, etc." />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Block</label>
              <input type="text" value={formData.block} onChange={e => setFormData({...formData, block: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Block" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Landmark</label>
              <input type="text" value={formData.landmark} onChange={e => setFormData({...formData, landmark: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Nearby landmark" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>City</label>
              <input type="text" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="City" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>State</label>
              <input type="text" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="State" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>ZIP/Postal Code</label>
              <input type="text" value={formData.zip} onChange={e => setFormData({...formData, zip: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="ZIP code" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>District ID</label>
              <input type="text" value={formData.district_id} onChange={e => setFormData({...formData, district_id: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="District ID" />
            </div>

            {/* Contact */}
            <div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>Contact Details</h3>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Email Address</label>
              <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Email address" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Fax</label>
              <input type="text" value={formData.fax} onChange={e => setFormData({...formData, fax: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Fax number" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Contact 1</label>
              <input type="text" value={formData.contact1} onChange={e => setFormData({...formData, contact1: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Primary contact number" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Contact 2</label>
              <input type="text" value={formData.contact2} onChange={e => setFormData({...formData, contact2: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Secondary contact number" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Contact Person</label>
              <input type="text" value={formData.contact_person} onChange={e => setFormData({...formData, contact_person: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Name of contact person" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Work ID</label>
              <input type="text" value={formData.work_id} onChange={e => setFormData({...formData, work_id: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Work ID" />
            </div>

            {/* Map */}
            <div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>
                Geo Location
                <span style={{ fontSize: 13, fontWeight: 400, color: C.g500, marginLeft: 12 }}>Click on the map to set location</span>
              </h3>
              
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <div style={{ flex: 1, padding: '10px 14px', background: C.g50, borderRadius: 8, border: `1px solid ${C.g200}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MapPin size={16} color={C.primary} />
                  <span style={{ fontSize: 14, color: C.g700 }}>Lat: <strong>{formData.lat.toFixed(6)}</strong></span>
                </div>
                <div style={{ flex: 1, padding: '10px 14px', background: C.g50, borderRadius: 8, border: `1px solid ${C.g200}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MapPin size={16} color={C.primary} />
                  <span style={{ fontSize: 14, color: C.g700 }}>Lng: <strong>{formData.lng.toFixed(6)}</strong></span>
                </div>
              </div>

              <div style={{ height: 400, width: '100%', borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.g200}` }}>
                <MapContainer center={[formData.lat, formData.lng]} zoom={10} style={{ height: '100%', width: '100%' }}>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <LocationPicker lat={formData.lat} lng={formData.lng} onLocationSelect={handleLocationSelect} />
                </MapContainer>
              </div>
            </div>

          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 32, paddingTop: 24, borderTop: `1px solid ${C.g100}` }}>
            <button
              type="button"
              onClick={() => navigate('/schools')}
              style={{ padding: '12px 24px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: C.g700 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
            >
              <Save size={18} /> {isEdit ? 'Update School' : 'Save School'}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
};

export default SchoolForm;
